import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Container,
	SxProps,
	Theme,
	Typography
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

export interface FAQItem {
	question: React.ReactNode;
	answer: React.ReactNode;
}

export interface FAQCategory {
	slug: string;
	title: string;
	/** Optional standing note displayed above this category's questions. */
	callout?: React.ReactNode;
	content: FAQItem[];
}

export interface FAQSectionProps {
	data: FAQCategory[];
	defaultCategory?: number;
	openFirstQuestion?: boolean;
	className?: string;
	sx?: SxProps<Theme>;
}

const FAQSection = ({ data, defaultCategory = 0, openFirstQuestion = true, className, sx }: FAQSectionProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const safeDefaultCategory = Math.min(Math.max(defaultCategory, 0), Math.max(data.length - 1, 0));
	const categoryParam = searchParams.get('category');
	const linkedCategory = categoryParam ? data.findIndex(category => category.slug === categoryParam) : -1;
	const activeCategory = linkedCategory >= 0 ? linkedCategory : safeDefaultCategory;
	const activeCategoryData = data[activeCategory];
	const activeContent = data[activeCategory]?.content ?? [];
	const [expandedQuestions, setExpandedQuestions] = React.useState<Set<number>>(
		() => new Set(openFirstQuestion && activeContent.length ? [0] : [])
	);
	const [isContentScrolling, setIsContentScrolling] = React.useState(false);
	const isContentScrollingRef = React.useRef(false);
	const hideScrollbarTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	React.useEffect(
		() => () => {
			if (hideScrollbarTimer.current) clearTimeout(hideScrollbarTimer.current);
		},
		[]
	);

	React.useEffect(() => {
		setExpandedQuestions(new Set(openFirstQuestion && activeContent.length ? [0] : []));
	}, [activeCategory, activeContent.length, openFirstQuestion]);

	const selectCategory = (index: number) => {
		const nextParams = new URLSearchParams(searchParams);
		nextParams.set('category', data[index].slug);
		setSearchParams(nextParams);
	};

	const toggleQuestion = (index: number, expanded: boolean) => {
		setExpandedQuestions(current => {
			const next = new Set(current);

			if (expanded) next.add(index);
			else next.delete(index);

			return next;
		});
	};

	const handleContentScroll = () => {
		if (!isContentScrollingRef.current) {
			isContentScrollingRef.current = true;
			setIsContentScrolling(true);
		}

		if (hideScrollbarTimer.current) clearTimeout(hideScrollbarTimer.current);

		hideScrollbarTimer.current = setTimeout(() => {
			isContentScrollingRef.current = false;
			setIsContentScrolling(false);
			hideScrollbarTimer.current = null;
		}, 2000);
	};

	if (!data.length) return null;

	return (
		<Box
			component="section"
			className={className}
			sx={[
				{
					backgroundColor: '#fbfbfb',
					py: { xs: 4, sm: 6, md: 9 }
				},
				...(Array.isArray(sx) ? sx : [sx])
			]}
		>
			<Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 4, md: 5 } }}>
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', md: '285px minmax(0, 1fr)' },
						gap: { xs: 4, md: 7, lg: 10 },
						alignItems: 'start'
					}}
				>
					<Box component="nav" aria-label="FAQ categories">
						{data.map((category, index) => {
							const isActive = activeCategory === index;

							return (
								<Box
									key={category.slug}
									component="button"
									type="button"
									onClick={() => selectCategory(index)}
									aria-pressed={isActive}
									sx={{
										display: 'block',
										width: '100%',
										m: 0,
										px: 0,
										py: { xs: 1.5, md: 2 },
										border: 0,
										borderBottom: `1px solid ${isActive ? '#0073de' : '#dedede'}`,
										borderRadius: 0,
										background: 'transparent',
										color: '#292929',
										fontFamily: 'DM Mono, monospace',
										fontSize: { xs: '0.875rem', sm: '1rem' },
										fontWeight: isActive ? 500 : 400,
										lineHeight: 1.35,
										letterSpacing: '-0.02em',
										textAlign: 'left',
										cursor: 'pointer',
										transition: 'color 160ms ease, border-color 160ms ease',
										'&:hover': { color: '#0073de' },
										'&:focus-visible': {
											position: 'relative',
											zIndex: 1,
											outline: '2px solid #0073de',
											outlineOffset: 2
										}
									}}
								>
									{category.title}
								</Box>
							);
						})}
					</Box>

					<Box
						aria-live="polite"
						onScroll={handleContentScroll}
						sx={{
							maxHeight: { xs: '65vh', sm: '70vh', md: 720 },
							overflowY: 'auto',
							overflowX: 'hidden',
							overscrollBehavior: 'contain',
							pr: { xs: 0.5, md: 1 },
							scrollbarGutter: 'stable',
							scrollbarWidth: 'thin',
							scrollbarColor: isContentScrolling ? 'rgba(41, 41, 41, 0.22) transparent' : 'transparent transparent',
							'&::-webkit-scrollbar': { width: 5 },
							'&::-webkit-scrollbar-track': { background: 'transparent' },
							'&::-webkit-scrollbar-thumb': {
								borderRadius: 999,
								backgroundColor: isContentScrolling ? 'rgba(41, 41, 41, 0.2)' : 'transparent',
								transition: 'background-color 200ms ease'
							},
							'&::-webkit-scrollbar-thumb:hover': {
								backgroundColor: isContentScrolling ? 'rgba(41, 41, 41, 0.34)' : 'transparent'
							}
						}}
					>
						<Box
							key={activeCategoryData.slug}
							sx={{
								animation: 'faq-category-enter 280ms cubic-bezier(0.22, 1, 0.36, 1)',
								'@keyframes faq-category-enter': {
									from: { opacity: 0, transform: 'translateY(8px)' },
									to: { opacity: 1, transform: 'translateY(0)' }
								},
								'@media (prefers-reduced-motion: reduce)': {
									animation: 'none'
								}
							}}
						>
							{activeCategoryData?.callout && (
								<Box
									role="note"
									sx={{
										display: 'flex',
										alignItems: 'flex-start',
										gap: { xs: 1.25, sm: 1.5 },
										mb: { xs: 1.5, md: 2 },
										p: { xs: 1.5, sm: 2 },
										borderRadius: '4px',
										backgroundColor: '#e2effc',
										color: '#0073de'
									}}
								>
									<InfoOutlinedIcon
										aria-hidden="true"
										sx={{ mt: '1px', flex: '0 0 auto', fontSize: { xs: 18, sm: 20 } }}
									/>
									<Typography
										component="div"
										sx={{
											fontFamily: 'DM Mono, monospace',
											fontSize: { xs: '0.75rem', sm: '0.8125rem' },
											fontWeight: 400,
											lineHeight: 1.55,
											letterSpacing: '-0.015em',
											overflowWrap: 'anywhere'
										}}
									>
										{activeCategoryData.callout}
									</Typography>
								</Box>
							)}

							{activeContent.map((item, index) => {
								const isExpanded = expandedQuestions.has(index);
								const summaryId = `faq-${activeCategoryData.slug}-${index}-header`;
								const panelId = `faq-${activeCategoryData.slug}-${index}-panel`;

								return (
									<Accordion
										key={`${activeCategory}-${index}`}
										expanded={isExpanded}
										onChange={(_, expanded) => toggleQuestion(index, expanded)}
										disableGutters
										sx={{
											mb: { xs: 1.25, md: 2 },
											border: '1px solid #e9e9e9',
											borderRadius: '8px !important',
											backgroundColor: isExpanded ? '#f5faff' : '#ffffff',
											boxShadow: '0 1px 3px rgba(0, 0, 0, 0.025)',
											overflow: 'hidden',
											transition: 'background-color 160ms ease',
											'&::before': { display: 'none' },
											'&.Mui-expanded': { m: { xs: '0 0 10px', md: '0 0 16px' } }
										}}
									>
										<AccordionSummary
											id={summaryId}
											aria-controls={panelId}
											expandIcon={
												isExpanded ? (
													<CloseIcon sx={{ color: '#0073de', fontSize: 20 }} />
												) : (
													<AddIcon sx={{ color: '#292929', fontSize: 22 }} />
												)
											}
											sx={{
												minHeight: { xs: 56, md: 76 },
												px: { xs: 2, sm: 2.5, md: 3 },
												py: { xs: 0.5, md: 0 },
												'&.Mui-expanded': { minHeight: { xs: 56, md: 76 } },
												'& .MuiAccordionSummary-content': { my: 1.5 },
												'& .MuiAccordionSummary-content.Mui-expanded': { my: 1.5 },
												'& .MuiAccordionSummary-expandIconWrapper': { ml: 1.5 }
											}}
										>
											<Typography
												component="h3"
												sx={{
													m: 0,
													fontFamily: 'DM Mono, monospace',
													fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
													fontWeight: isExpanded ? 500 : 400,
													lineHeight: 1.35,
													letterSpacing: '-0.02em',
													color: '#292929'
												}}
											>
												{item.question}
											</Typography>
										</AccordionSummary>

										<AccordionDetails sx={{ px: { xs: 2, sm: 2.5, md: 3 }, pt: 0, pb: { xs: 2, md: 3 } }}>
											<Typography
												component="div"
												sx={{
													fontFamily: 'DM Mono, monospace',
													fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
													fontWeight: 400,
													lineHeight: 1.55,
													letterSpacing: '-0.015em',
													color: '#6c6c6c'
												}}
											>
												{item.answer}
											</Typography>
										</AccordionDetails>
									</Accordion>
								);
							})}
						</Box>
					</Box>
				</Box>
			</Container>
		</Box>
	);
};

export default FAQSection;
